import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, CheckCircle2, XCircle, RotateCcw, ShieldCheck, AlertTriangle,
  ListChecks, FolderSearch, Lightbulb, Loader2, Lock, GanttChartSquare, Brain,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PriorityBadge, StatusBadge, Label } from "@/components/common";
import { relativeTime } from "@/lib/format";
import CpmGantt from "@/components/CpmGantt";

export default function ApprovalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState("");

  const { data: a, isLoading } = useQuery({
    queryKey: ["approval", id],
    queryFn: async () => (await api.get(`/approvals/${id}`)).data,
  });

  const canApprove = user?.role === "owner" || user?.role === "admin";

  const act = async (action) => {
    setBusy(action);
    try {
      await api.post(`/approvals/${id}/${action}`, { notes });
      const verb = { approve: "approved & queued for delivery", reject: "rejected", revise: "sent back for revision" }[action];
      toast.success(`Deliverable ${verb}.`);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    } finally {
      setBusy("");
    }
  };

  if (isLoading || !a) return <div className="text-sm text-ink-muted">Loading approval…</div>;
  const decided = a.status !== "pending";

  return (
    <div data-testid="approval-detail-page">
      <button onClick={() => navigate("/approvals")} className="flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink mb-4">
        <ArrowLeft className="w-4 h-4" /> Approval Center
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT — evidence / preview */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-border">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-ink text-white">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5" style={{ color: "#7CA0FF" }} />
                <div>
                  <h1 className="font-heading text-lg font-bold tracking-tight">{a.deliverable}</h1>
                  <p className="text-xs text-white/60">{a.client_name} · v{a.version}</p>
                </div>
              </div>
              <PriorityBadge value={a.priority} />
            </div>

            {/* Document mock preview */}
            <div className="p-6">
              <div className="border border-border bg-surface-raised p-6 min-h-[160px]">
                <div className="text-[10px] uppercase tracking-[0.2em] text-ink-faint font-semibold mb-2">Deliverable Preview</div>
                <h2 className="font-heading text-xl font-bold text-ink">{a.deliverable}</h2>
                <p className="text-sm text-ink-muted mt-2 leading-relaxed">{a.summary}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {a.sources?.map((s) => (
                    <span key={s} className="inline-flex items-center gap-1 bg-white border border-border px-2 py-1 text-[11px] font-mono text-ink-muted">
                      <FileText className="w-3 h-3" /> {s}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* CPM if scheduling */}
          {a.schedule && (
            <div className="bg-white border border-border p-5" data-testid="approval-cpm">
              <div className="flex items-center gap-2 mb-4">
                <GanttChartSquare className="w-4 h-4 text-brand" />
                <h3 className="font-heading text-sm font-semibold uppercase tracking-tight">Critical Path Schedule</h3>
              </div>
              <CpmGantt schedule={a.schedule} />
            </div>
          )}

          {/* Key decisions */}
          <div className="bg-white border border-border p-5">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-4 h-4 text-brand" />
              <h3 className="font-heading text-sm font-semibold uppercase tracking-tight">Key Decisions Made</h3>
            </div>
            <ul className="space-y-2">
              {a.decisions?.map((d, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-ink">
                  <span className="font-mono text-xs text-ink-faint mt-0.5">{String(i + 1).padStart(2, "0")}</span> {d}
                </li>
              ))}
            </ul>
          </div>

          {/* Sources used */}
          <div className="bg-white border border-border p-5">
            <div className="flex items-center gap-2 mb-3">
              <FolderSearch className="w-4 h-4 text-brand" />
              <h3 className="font-heading text-sm font-semibold uppercase tracking-tight">Sources Used (Evidence)</h3>
            </div>
            {a.sources?.length ? (
              <div className="space-y-1.5">
                {a.sources.map((s) => (
                  <div key={s} className="flex items-center gap-2 text-sm text-ink-muted border border-border px-3 py-2 bg-surface-raised">
                    <FileText className="w-3.5 h-3.5 text-ink-faint" /> <span className="font-mono text-xs">{s}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-ink-faint">No source files linked.</p>}
          </div>
        </div>

        {/* RIGHT — actions */}
        <div className="space-y-4">
          {/* Owner Mind prediction */}
          {a.prediction && (
            <div className="bg-white border border-brand p-4" data-testid="prediction-panel">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="w-4 h-4 text-brand" />
                <h3 className="font-heading text-sm font-semibold uppercase tracking-tight text-ink">Owner Mind Predicts</h3>
                {a.prediction.source === "llm" && <span className="text-[10px] font-semibold uppercase tracking-wider border border-brand text-brand px-1.5 py-0.5">Claude Sonnet 4.6</span>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-bold ${a.prediction.action === "approve" ? "text-st-success" : a.prediction.action === "reject" ? "text-st-critical" : "text-st-warning"}`}>{a.prediction.label}</span>
                <span className="text-xs font-mono text-ink-faint">{a.prediction.confidence}% confidence</span>
              </div>
              <p className="text-xs text-ink-muted mt-1">{a.prediction.rationale}</p>
            </div>
          )}

          {/* Risks */}
          <div className="bg-st-warning/10 border border-st-warning p-4" data-testid="risk-panel">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-st-warning" />
              <h3 className="font-heading text-sm font-semibold uppercase tracking-tight text-ink">Risks & Issues</h3>
            </div>
            {a.risks?.length ? (
              <ul className="space-y-1.5">
                {a.risks.map((r, i) => <li key={i} className="text-sm text-ink flex items-start gap-2"><span className="text-st-warning">▸</span> {r}</li>)}
              </ul>
            ) : <p className="text-sm text-ink-muted">No risks flagged.</p>}
          </div>

          {/* Validation */}
          <div className="bg-white border border-border p-4">
            <div className="flex items-center gap-2 mb-3">
              <ListChecks className="w-4 h-4 text-brand" />
              <h3 className="font-heading text-sm font-semibold uppercase tracking-tight">Validation Checklist</h3>
            </div>
            <div className="space-y-2">
              {a.validation?.map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  {c.passed ? <CheckCircle2 className="w-4 h-4 text-st-success shrink-0" /> : <XCircle className="w-4 h-4 text-st-warning shrink-0" />}
                  <span className={c.passed ? "text-ink" : "text-ink-muted"}>{c.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white border-2 border-ink p-4" data-testid="action-panel">
            <Label className="mb-2">Owner Decision</Label>
            {decided ? (
              <div className="text-center py-4">
                <StatusBadge value={a.status} />
                <p className="text-xs text-ink-muted mt-2">Decision recorded {relativeTime(a.updated_at)}.</p>
              </div>
            ) : !canApprove ? (
              <div className="flex items-start gap-2 bg-surface-muted border border-border p-3">
                <Lock className="w-4 h-4 text-ink-faint mt-0.5" />
                <p className="text-xs text-ink-muted">Only the Owner can approve client-facing deliverables. Your role: <b>{user?.role}</b>.</p>
              </div>
            ) : (
              <>
                <textarea
                  value={notes} onChange={(e) => setNotes(e.target.value)}
                  data-testid="approval-notes"
                  placeholder="Add owner notes (optional)…"
                  className="w-full border border-border px-3 py-2 text-sm mb-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" rows={3}
                />
                <button
                  onClick={() => act("approve")} disabled={!!busy} data-testid="approve-btn"
                  className="w-full flex items-center justify-center gap-2 bg-st-success text-white py-2.5 text-sm font-semibold hover:brightness-110 disabled:opacity-60 mb-2 transition-all"
                >
                  {busy === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Approve & Deliver
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => act("revise")} disabled={!!busy} data-testid="revise-btn" className="flex items-center justify-center gap-1.5 border border-st-warning text-st-warning py-2 text-sm font-medium hover:bg-st-warning/10 disabled:opacity-60 transition-colors">
                    {busy === "revise" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Revise
                  </button>
                  <button onClick={() => act("reject")} disabled={!!busy} data-testid="reject-btn" className="flex items-center justify-center gap-1.5 border border-st-critical text-st-critical py-2 text-sm font-medium hover:bg-st-critical/10 disabled:opacity-60 transition-colors">
                    {busy === "reject" ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Reject
                  </button>
                </div>
              </>
            )}
          </div>

          {/* History */}
          {a.history?.length > 0 && (
            <div className="bg-white border border-border p-4">
              <Label className="mb-2">Decision History</Label>
              <div className="space-y-2">
                {a.history.map((h, i) => (
                  <div key={i} className="text-xs border-l-2 border-border pl-2">
                    <span className="font-semibold text-ink capitalize">{h.action}</span> by {h.by}
                    {h.notes && <p className="text-ink-muted">"{h.notes}"</p>}
                    <span className="font-mono text-ink-faint">{relativeTime(h.at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
